import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { InterviewLayout } from '../components/templates/InterviewLayout.js';
import { Button } from '../components/atoms/Button.js';
import { Spinner } from '../components/atoms/Spinner.js';
import { Typography } from '../components/atoms/Typography.js';
import { Card } from '../components/atoms/Card.js';
import { ErrorBanner } from '../components/molecules/ErrorBanner.js';
import { get, ApiClientError } from '../lib/api-client.js';
import type { ApiResponse } from '../types/api.js';
import type { InterviewSession } from '../types/interview.js';

interface SurveyInfo {
  title: string;
  description: string;
  totalQuestions: number;
}

export function LandingPage() {
  const { t } = useTranslation();
  const { t: tInterview } = useTranslation('interview');
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [survey, setSurvey] = useState<SurveyInfo | null>(null);

  useEffect(() => {
    if (!token) return;

    async function fetchSurvey() {
      try {
        setLoading(true);
        const res = await get<ApiResponse<InterviewSession>>(`/api/v1/interviews/${token}/status`);
        setSurvey({
          title: tInterview('title'), // Title from session context
          description: '',
          totalQuestions: res.data.totalQuestions,
        });
      } catch (err) {
        if (err instanceof ApiClientError) {
          setError(err.message);
        } else {
          setError(tInterview('landing.load_error'));
        }
      } finally {
        setLoading(false);
      }
    }

    void fetchSurvey();
  }, [token, tInterview]);

  const handleStart = () => {
    navigate(`/survey/${token}/consent`);
  };

  if (loading) {
    return (
      <InterviewLayout>
        <div className="flex flex-1 items-center justify-center">
          <Spinner size="lg" />
        </div>
      </InterviewLayout>
    );
  }

  if (error) {
    return (
      <InterviewLayout>
        <div className="flex flex-1 items-center justify-center">
          <ErrorBanner message={error} onRetry={() => window.location.reload()} />
        </div>
      </InterviewLayout>
    );
  }

  return (
    <InterviewLayout title={survey?.title}>
      <div className="flex flex-1 flex-col items-center justify-center gap-6">
        <Card padding="lg" className="w-full text-center">
          <Typography variant="h2" className="mb-4">
            {survey?.title ?? tInterview('title')}
          </Typography>
          {survey?.description && (
            <Typography variant="body" className="mb-6">
              {survey.description}
            </Typography>
          )}
          {survey?.totalQuestions != null && survey.totalQuestions > 0 && (
            <Typography variant="body-sm" className="mb-6">
              {tInterview('landing.questions_count', { count: survey.totalQuestions })}
            </Typography>
          )}
          <Button size="lg" onClick={handleStart}>
            {t('button.start')}
          </Button>
        </Card>
      </div>
    </InterviewLayout>
  );
}
